import type {
  IClientAddPayload,
  IClientAddResponse,
} from "../types/client-add.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addClient, editClient } from "./client-add.api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";
import { useNavigate } from "@tanstack/react-router";
import type {
  IClientEditPayload,
  IClientEditResponse,
} from "../types/client.types";

export const useAddClient = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<IClientAddResponse, Error, IClientAddPayload>({
    mutationFn: addClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client added successfully");
      navigate({ to: "/admin/client" });
    },

    onError: (err: any) => {
      toast.error("Failed to add client", {
        description: extractErrorMessage(err),
      });
    },
  });
};

export type EditClientVariables = {
  userId: string;
} & IClientEditPayload;

export const useEditClient = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<IClientEditResponse, Error, EditClientVariables>({
    mutationFn: ({ userId, ...payload }) => editClient(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-overview"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["client-activity-log"] });
      queryClient.invalidateQueries({ queryKey: ["client-tokens"] });
      toast.success("Client updated successfully");
      navigate({ to: "/admin/client" });
    },
    onError: (err: any) => {
      toast.error("Failed to update client", {
        description: extractErrorMessage(err),
      });
    },
  });
};
